import React, { useState } from 'react';
import { Loader2, ShieldCheck } from 'lucide-react';
import { inquiriesAPI } from '../../services/api';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

const VERDICTS = [
  { value: 'feasible', label: 'Feasible', className: 'bg-emerald-100 text-emerald-800' },
  { value: 'feasible_with_changes', label: 'Feasible with changes', className: 'bg-amber-100 text-amber-800' },
  { value: 'not_feasible', label: 'Not feasible', className: 'bg-red-100 text-red-800' },
];

export const InquiryFeasibilityCard = ({ inquiryId, review, documents, onRefresh }) => {
  const [form, setForm] = useState({
    verdict: review?.verdict || '',
    reviewer_name: review?.reviewer_name || '',
    notes: review?.notes || '',
    document_id: review?.document_id || '',
  });
  const [saving, setSaving] = useState(false);

  const setField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const current = VERDICTS.find((item) => item.value === review?.verdict);

  const save = async () => {
    if (!form.verdict) {
      toast.error('Choose a feasibility verdict');
      return;
    }
    setSaving(true);
    try {
      await inquiriesAPI.saveFeasibility(inquiryId, {
        verdict: form.verdict,
        reviewer_name: form.reviewer_name || undefined,
        notes: form.notes || undefined,
        document_id: form.document_id || undefined,
      });
      toast.success('Feasibility review saved');
      await onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save the feasibility review');
    } finally { setSaving(false); }
  };

  return (
    <div className="border-t bg-slate-50/60 px-4 py-4" data-testid="inquiry-feasibility-card">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-blue-700" />
        <p className="font-heading font-bold">Feasibility verdict</p>
        {current && <Badge className={current.className} data-testid="inquiry-feasibility-current">{current.label}</Badge>}
        {review?.reviewed_at && (
          <span className="text-xs text-muted-foreground" data-testid="inquiry-feasibility-meta">
            {review.reviewer_name} · {new Date(review.reviewed_at).toLocaleString('en-IN')}
          </span>
        )}
      </div>
      <div className="grid gap-3 lg:grid-cols-4">
        <div>
          <Label className="text-xs">Verdict *</Label>
          <select className="mt-1 h-10 w-full border bg-white px-2 text-sm" value={form.verdict}
            onChange={(event) => setField('verdict', event.target.value)} data-testid="feasibility-verdict-select">
            <option value="">Select verdict</option>
            {VERDICTS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs">Reviewed by</Label>
          <Input value={form.reviewer_name} onChange={(event) => setField('reviewer_name', event.target.value)}
            placeholder="Your name" data-testid="feasibility-reviewer-input" />
        </div>
        <div>
          <Label className="text-xs">Linked document</Label>
          <select className="mt-1 h-10 w-full border bg-white px-2 text-sm" value={form.document_id}
            onChange={(event) => setField('document_id', event.target.value)} data-testid="feasibility-document-select">
            <option value="">None</option>
            {documents.map((document) => <option key={document.id} value={document.id}>{document.filename}</option>)}
          </select>
        </div>
        <div className="flex items-end">
          <Button onClick={save} disabled={saving} className="w-full" data-testid="save-feasibility-button">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save review
          </Button>
        </div>
        <div className="lg:col-span-4">
          <Label className="text-xs">Notes</Label>
          <Textarea rows={2} value={form.notes} onChange={(event) => setField('notes', event.target.value)}
            placeholder="Constraints, required changes, tooling needs…" data-testid="feasibility-notes-input" />
        </div>
      </div>
    </div>
  );
};
